/**
 * Scroll reveal: adds `.is-visible` to `[data-reveal]` elements as they enter
 * the viewport. Sections style `[data-reveal]` as `opacity: 0` and only restore
 * it on `.is-visible`, so every element MUST end up with the class — including
 * when animation is suppressed.
 */
(function () {
  const SELECTOR = '[data-reveal]';
  const VISIBLE = 'is-visible';
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const inDesignMode = typeof Shopify !== 'undefined' && Shopify.designMode;

  /*
   * Fail-safe: the hidden initial state (`opacity: 0` on [data-reveal]) is
   * gated in CSS behind `html.reveal-armed`. Arming here — and only when the
   * animation will actually run — means a blocked, failed, or not-yet-loaded
   * script leaves every section fully visible instead of blank. Without this
   * the 4D experience page rendered as an empty dark block until JS ran.
   */
  if (
    !motionQuery.matches &&
    'IntersectionObserver' in window &&
    !document.hidden &&
    !inDesignMode
  ) {
    document.documentElement.classList.add('reveal-armed');
  }

  let observer = null;
  let observedCount = 0;

  function reveal(el) {
    el.classList.add(VISIBLE);
  }

  /**
   * Reduced motion still has to show the content. Sections hide `[data-reveal]`
   * with `opacity: 0` until `.is-visible` lands, so skipping the reveal entirely
   * would leave whole sections permanently blank.
   */
  function revealAll() {
    document.querySelectorAll(SELECTOR).forEach(reveal);
  }

  function releaseObserver() {
    // Long pages can carry dozens of targets. Once everything has been revealed
    // there is nothing left to watch, so drop the observer rather than leaving
    // it attached to the document for the rest of the session.
    if (observedCount > 0 || observer === null || inDesignMode) return;
    observer.disconnect();
    observer = null;
  }

  function getObserver() {
    if (observer) return observer;

    observer = new IntersectionObserver(
      (entries, self) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          reveal(entry.target);
          self.unobserve(entry.target);
          observedCount -= 1;
        });
        releaseObserver();
      },
      { threshold: 0.15, rootMargin: '0px 0px -40px 0px' }
    );

    return observer;
  }

  function observeElements() {
    if (motionQuery.matches) {
      revealAll();
      return;
    }

    if (!('IntersectionObserver' in window)) {
      revealAll();
      return;
    }

    const targets = document.querySelectorAll(SELECTOR);
    if (!targets.length) return;

    const io = getObserver();
    targets.forEach((el) => {
      if (el.classList.contains(VISIBLE)) return;
      observedCount += 1;
      io.observe(el);
    });

    releaseObserver();
  }

  function onMotionPreferenceChange() {
    if (!motionQuery.matches) {
      observeElements();
      return;
    }
    revealAll();
    observedCount = 0;
    if (observer) {
      observer.disconnect();
      observer = null;
    }
  }

  if (typeof motionQuery.addEventListener === 'function') {
    motionQuery.addEventListener('change', onMotionPreferenceChange);
  } else if (typeof motionQuery.addListener === 'function') {
    motionQuery.addListener(onMotionPreferenceChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeElements, { once: true, passive: true });
  } else {
    observeElements();
  }

  /*
   * Dead-man's switch. IntersectionObserver callbacks are frozen while
   * document.visibilityState is "hidden", and some embedded webviews (LINE's
   * in-app browser among them) report hidden indefinitely — the gate arms,
   * content fades to opacity 0, and the observer never brings it back. If no
   * element has been revealed shortly after startup, the observer is presumed
   * dead: drop the gate class so every hidden rule stops applying at once.
   * When the observer is alive it will have revealed the above-the-fold
   * elements well inside this window, and the timer does nothing.
   */
  setTimeout(function () {
    if (!document.querySelector(SELECTOR + '.' + VISIBLE)) {
      document.documentElement.classList.remove('reveal-armed');
    }
  }, 1200);

  /*
   * IO callbacks freeze while the document is hidden, and some webviews
   * (LINE's in-app browser) report hidden indefinitely even in the
   * foreground. A page that revealed its first sections and then went
   * hidden would show blank sections to anyone scrolling it there. Reveal
   * everything the moment the document hides — animations are invisible in
   * a hidden document, so nothing is lost.
   */
  document.addEventListener('visibilitychange', function () {
    if (document.hidden) revealAll();
  }, { passive: true });

  /*
   * The theme editor is never allowed to hide anything. Its preview iframe goes
   * through hidden→visible transitions on every navigation and re-renders
   * sections after this script has run, so any element that misses the observer
   * would sit at opacity 0 forever and the section would look empty to whoever
   * is editing it. Reveal on sight instead, and again after each re-render.
   */
  if (inDesignMode) {
    document.documentElement.classList.remove('reveal-armed');
    revealAll();
    document.addEventListener(
      'shopify:section:load',
      function () {
        document.documentElement.classList.remove('reveal-armed');
        revealAll();
      },
      { passive: true }
    );
  }
})();
